import React, { useEffect, useState } from "react";
import DateFilter from "./DateFilter";
import dayjs, { Dayjs } from "dayjs";
import Header from "../../components/dms_common/Header";
import "./DeliveryConfirmation.css";
import { fetchDeliverySummaryWithInvoice } from "./DeliveryConfirmationService";
import DeliveryTable from "./DeliveryTable";
import DeliverySummary from "./DeliverySummary";
import StatusFilter from "./StatusFilter";
import { DeliveryInvoiceDetailType, DeliveryInvoiceSummary } from "../../types/types";
import { Loader } from "../../components/loader/Loader";


const DeliveryConfirmation = () => {
  const [summary, setSummary] = useState<DeliveryInvoiceSummary>({
    total: 0,
    pending: 0,
    delivered: 0,
    partiallyDelivered: 0,
    cancelled: 0,
  });
  const [allData, setAllData] = useState<DeliveryInvoiceDetailType[]>([]);
  const [filteredData, setFilteredData] = useState<DeliveryInvoiceDetailType[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: dayjs().subtract(7, 'day').format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),});
  
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Calculate summary for the status types
  const calculateSummary = (data: DeliveryInvoiceDetailType[]) => {
    const total = data.length;
    const pending = data.filter((invoice) => invoice.status === "INVOICED").length;
    const delivered = data.filter((invoice) => invoice.status === "DELIVERED").length;
    const partiallyDelivered = data.filter((invoice) => invoice.status === "PARTIAL DELIVERED").length;
    const cancelled = data.filter((invoice) => invoice.status === "CANCELLED").length;
    setSummary({ total, pending, delivered, partiallyDelivered, cancelled });
  };

 // Fetch data when dateRange changes
 useEffect(() => {
  const fetchFilteredData = async () => {
    try {
      setIsLoading(true);
      const data = await fetchDeliverySummaryWithInvoice(dateRange.startDate, dateRange.endDate);
      setAllData(data);
      setFilteredData(data); // Update filtered data
      calculateSummary(data); // Update summary
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    }
  };

  fetchFilteredData();
}, [dateRange]);

  const handleApply = (startDate: Dayjs, endDate: Dayjs) => {
    setDateRange({
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    });
  };

  const handleReset = () => {
    setDateRange({
      startDate: dayjs().subtract(7, 'day').format("YYYY-MM-DD"),
      endDate: dayjs().format("YYYY-MM-DD"),
    });
  };

  useEffect(()=>{
    let filtered = [...allData];

    if(statusFilter !== "ALL"){
      filtered = filtered.filter((invoice)=>invoice.status?.toLowerCase() === statusFilter.toLowerCase());
    }
    
    if(searchText){
      const searchKey = searchText.toLowerCase();
      filtered = filtered.filter(
        (invoice) => 
          invoice.invoiceNumber.toLowerCase().includes(searchKey) ||
          invoice.loadNumber.toLowerCase().includes(searchKey) ||
          invoice.status.toLowerCase().includes(searchKey) ||
          invoice.outletCode.toLowerCase().includes(searchKey) ||
          invoice.outletName.toLowerCase().includes(searchKey) 
      )
    }


    setFilteredData(filtered);
  },[searchText,allData,statusFilter])

  const refreshData = ()=>{
    setDateRange({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    });
  }

  const statusFilterMenuItems = [
    {
      id:"ALL",
      label:"All Orders"
    },
    {
      id:"Invoiced",
      label:"Pending"
    },
    {
      id:"Delivered",
      label:"Delivered"
    },
    {
      id:"Partial Delivered",
      label:"Partially Delivered"
    },
    {
      id:"Cancelled",
      label:"Cancelled"
    }
  ]

  return (
    <div className="delivery-confirmation-container">
      <Header title={"Delivery Confirmation"} searchFunction={setSearchText} />
      <div className="delivery-confirmation-container-date">
        <DateFilter onApply={handleApply} onReset={handleReset} maxDays={30}></DateFilter>
      </div>
    
      {/* Delivery Summary */}
      <div className="delivery-summary-filter">
        <DeliverySummary invoiceSummary={summary}/>
        <StatusFilter onStatusChange={setStatusFilter} statusFilterMenuItems={statusFilterMenuItems}/>
      </div>

      {isLoading ? 
        <Loader />
      :
        <>
          <div style={{color:"rgb(124 124 124 / 72%)", fontSize: "14px", marginTop:"15px"}}>Click on the edit button to change the delivery status</div>
          {/* Delivery Table */}
          <DeliveryTable data={filteredData} refreshData={refreshData} />
        </>
      }
      
    </div>
  );
};

export default DeliveryConfirmation;